import { effect, Injectable, signal } from '@angular/core';
import { SearchService } from './search.service';
import { TerritoryTreeService } from './territory-tree.service';
import { TerritoryCountersService } from './territory-counters.service';
import { firstValueFrom } from 'rxjs';
import { ArticleImSearchService } from './article-im-search.service';
import { TreeviewItem } from '../../lib/ngx-treeview';
import { Period } from '../../common-explain/models/filter-date';
import { EntityService } from './entity.service';
import { Impacter } from '../../models/impacter/impacter';
import { TerritoryHelper } from '../helpers/territory.helper';

export enum SmartSearchSource {
  PRESS = 'article',
  ADMIN_DOC = 'admin_doc',
  WEB = 'web_content',
}

export const SMART_SEARCH_SESSIONS_KEY = 'smart-search-sessions';
export const TAB_ID_KEY = 'smart-search-tab-id';

export interface SmartSearchConfig {
  topic?: string;
  topicId?: number;
  perimeter?: string[];
  territories?: string[];
  territoryTree?: TreeviewItem[];
  adminDocSources?: string[];
  pressSources?: string[];
  keywords?: string;
  impacterId?: string | null;
  period?: Period;
  impacterName?: string | null;
  fullPerimeter?: string;
  displayedPerimeter?: string;
  promptInputs?: {
    subject?: string;
    impacter?: string;
    company?: string;
  };
  promptBase?: string;
  prompt?: string;
  promptId?: number;
  promptType?: 'default' | 'user';
  sources?: SmartSearchSource[];
}

@Injectable({
  providedIn: 'root',
})
export class SmartSearchService {
  panelOpen = signal(false);
  context = signal<SmartSearchConfig>({});
  impacter = signal<Impacter | null>(null);
  searchConfig = signal<any | undefined>(undefined);

  constructor(
    private searchService: SearchService,
    private articleImSearchService: ArticleImSearchService,
    private territoryTreeService: TerritoryTreeService,
    private territoryCountersService: TerritoryCountersService,
    private entityService: EntityService
  ) {
    this.entityService.getImpacter().subscribe(impacter => {
      this.impacter.set(impacter);
    });
    this.articleImSearchService.getSearch().subscribe(search => {
      this.searchConfig.set(search);
    });
    this.searchService.getSearch().subscribe((search: any) => {
      this.searchConfig.set(search);
    });
    effect(async () => {
      const searchConfig = this.searchConfig();
      if (searchConfig) {
        await this.updateContext(searchConfig, this.impacter());
      }
    });
  }

  async updateContext(search: any, impacter: Impacter | null) {
    const checkedTerritories = search.filters?.territories || [];
    const perimeter = search.perimeter;
    const adminDocSources = search.filters?.source;
    const period = search.filters?.period;

    const territoryTree = this.territoryTreeService.getTerritoryTree();

    let fullPerimeter: string | undefined = undefined;
    let displayedPerimeter: string | undefined = undefined;
    if (territoryTree && territoryTree.length > 0) {
      [fullPerimeter, displayedPerimeter] = TerritoryHelper.buildPerimeterRepresentations(territoryTree);
    }

    let topic: { id: number; name: string } | undefined, keywords: string | undefined;
    const service = impacter?.id ? this.articleImSearchService : this.searchService;
    if (service.searchValue) {
      if (typeof service.searchValue === 'string') {
        keywords = service.searchValue;
      } else {
        topic = service.searchValue;
      }
    }
    let pressSources: string[] = [];
    await firstValueFrom(this.territoryCountersService.getTerritoryCounters()).then(territoryCounters => {
      pressSources = territoryCounters.sourcesTerritories.map(source => source.name);
    });
    this.context.set({
      territories: checkedTerritories,
      perimeter: perimeter,
      territoryTree: territoryTree,
      fullPerimeter: fullPerimeter,
      displayedPerimeter: displayedPerimeter,
      adminDocSources: adminDocSources,
      pressSources: pressSources,
      topic: topic?.name,
      topicId: topic?.id,
      keywords: keywords,
      impacterId: impacter?.id.toString() ?? null,
      impacterName: impacter?.name ?? null,
      period: period,
    });
  }

  toggleSmartSearchTrigger() {
    if (this.panelOpen()) {
      this.panelOpen.set(false);
    } else {
      this.panelOpen.set(true);
    }
  }

  resetSearchSession(): void {
    const tabId = sessionStorage.getItem(TAB_ID_KEY);
    if (tabId) {
      const sessionsStr = localStorage.getItem(SMART_SEARCH_SESSIONS_KEY) || '{}';
      const sessions = JSON.parse(sessionsStr);
      if (sessions[tabId]) {
        delete sessions[tabId];
        localStorage.setItem(SMART_SEARCH_SESSIONS_KEY, JSON.stringify(sessions));
      }
      sessionStorage.removeItem(TAB_ID_KEY);
    }
  }
}
